import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import LandingNav from './landing/LandingNav';
import LandingHero from './landing/LandingHero';
import LandingTrustStrip from './landing/LandingTrustStrip';
import LandingBookingSection from './landing/LandingBookingSection';
import SpecialtyBrowse from './landing/SpecialtyBrowse';
import DoctorsDirectory from './landing/DoctorsDirectory';
import LeadershipSection from './landing/LeadershipSection';
import LandingAbout from './landing/LandingAbout';
import LandingFooter from './landing/LandingFooter';
import { groupDoctorsBySpecialty } from './landing/specialtyMeta';
import { DEFAULT_TENANT_SLUG } from '../config/singleTenant';
import { IITH, heroBackground } from './landing/iithBrand';

const API = process.env.REACT_APP_BACKEND_URL;

function scrollToBook() {
  document.getElementById('book-section')?.scrollIntoView({ behavior: 'smooth' });
}

export default function PublicClinicLandingPage() {
  const { slug: slugParam } = useParams();
  const slug = (slugParam || DEFAULT_TENANT_SLUG || '').toLowerCase();
  const gotoLogin = () => window.location.assign('/login');

  const [tenant, setTenant] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookPreset, setBookPreset] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const [tRes, dRes] = await Promise.allSettled([
          axios.get(`${API}/api/tenant/resolve`, { params: { slug } }),
          axios.get(`${API}/api/tenant/${slug}/doctors`),
        ]);
        if (cancelled) return;
        if (tRes.status === 'fulfilled') setTenant(tRes.value.data);
        else setError('Clinic not found');
        if (dRes.status === 'fulfilled') setDoctors(dRes.value.data || []);
      } catch (_) {
        if (!cancelled) setError('Clinic not found');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const bookableDoctors = useMemo(
    () => doctors.filter((d) => d.bookable),
    [doctors],
  );
  const specialtyGroups = useMemo(() => groupDoctorsBySpecialty(doctors), [doctors]);
  const bookableSpecialtyGroups = useMemo(
    () => groupDoctorsBySpecialty(bookableDoctors),
    [bookableDoctors],
  );

  const startBooking = (specialty, doctorName = '') => {
    // Online booking is GP-only; ignore non-bookable specialty shortcuts.
    const allowed = bookableSpecialtyGroups.some((g) => g.specialty === specialty);
    if (!allowed && specialty) {
      const fallback = bookableSpecialtyGroups[0]?.specialty || 'General Physician';
      setBookPreset({ specialty: fallback, doctorName: '', key: Date.now() });
    } else {
      setBookPreset({ specialty, doctorName, key: Date.now() });
    }
    scrollToBook();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ background: IITH.ink }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: IITH.saffron }} />
      </div>
    );
  }
  if (error || !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ background: IITH.ink }}>
        <p className="text-lg">Hospital site unavailable</p>
      </div>
    );
  }

  const b = tenant.branding || {};
  const name = b.display_name || tenant.name || IITH.hospital;
  const primary = b.primary_color || IITH.saffron;
  const logo = b.logo_url || b.prescription_logo_url;
  const highlights = (b.about_highlights || []).filter(Boolean);
  const heroStyle = heroBackground(primary, b.hero_image_url);
  const address = b.contact_address || IITH.campus;

  return (
    <div className="min-h-screen text-white" style={{ background: IITH.ink }} data-testid="public-clinic-landing">
      <LandingNav
        name={name}
        logo={logo}
        primary={primary}
        gotoLogin={gotoLogin}
        onAppointments={scrollToBook}
        showAbout
      />
      <LandingHero
        name={name}
        logo={logo}
        primary={primary}
        tagline={b.tagline || IITH.tagline}
        heroStyle={heroStyle}
        onBook={scrollToBook}
        onSpecialties={() => document.getElementById('specialties')?.scrollIntoView({ behavior: 'smooth' })}
      />
      <LandingTrustStrip
        primary={primary}
        specialtyCount={specialtyGroups.length}
        doctorCount={doctors.length}
        address={address}
      />
      <LandingBookingSection
        slug={slug}
        primary={primary}
        bookPreset={bookPreset}
        doctorProfiles={bookableDoctors}
      />
      <SpecialtyBrowse
        groups={bookableSpecialtyGroups}
        primary={primary}
        onSelectSpecialty={(s) => startBooking(s)}
      />
      <DoctorsDirectory doctors={doctors} primary={primary} onBookDoctor={(s, n) => startBooking(s, n)} />
      <LeadershipSection />
      <LandingAbout name={name} primary={primary} aboutLong={b.about_long} highlights={highlights} />
      <LandingFooter name={name} logo={logo} primary={primary} />
    </div>
  );
}
